import { useState, type ReactNode } from "react";
// @ts-expect-error pure js
import { useConfirm } from "./use-confirm.jsx";
// @ts-expect-error pure js
import useAuthenticatedAxios from "./use-authenticated-axios";
import Text from "../components/UI/Text";

// Champs du bien dont les pièces alimentent les items d'état des lieux
// (mêmes champs que generate_edl_items côté backend)
const EDL_PIECES_FIELDS = [
  "autres_pieces",
  "locaux_privatifs",
  "logement_espaces_privatifs",
] as const;

type PiecesHolder = Partial<
  Record<(typeof EDL_PIECES_FIELDS)[number], string[] | null>
>;

export function getAddedEdlPieces(
  before: PiecesHolder | undefined,
  after: PiecesHolder | undefined,
): string[] {
  const beforePieces = new Set(
    EDL_PIECES_FIELDS.flatMap((field) => before?.[field] ?? []).map((piece) =>
      piece.toLowerCase(),
    ),
  );
  const added = EDL_PIECES_FIELDS.flatMap(
    (field) => after?.[field] ?? [],
  ).filter((piece) => !beforePieces.has(piece.toLowerCase()));
  return [...new Set(added)];
}

/**
 * Après l'édition d'un bien, propose de créer dans l'état des lieux les pièces
 * qui viennent d'être ajoutées. Ne supprime jamais rien quand une pièce est
 * retirée. Rendre `confirmDialog` dans le JSX du composant hôte.
 */
export default function useEdlPiecesPrompt(propertyId: number | undefined): {
  confirmDialog: ReactNode;
  promptIfPiecesAdded: (before: PiecesHolder, after: PiecesHolder) => void;
} {
  const [Confirm, openConfirm] = useConfirm();
  const axios = useAuthenticatedAxios();
  const [pieces, setPieces] = useState<string[]>([]);

  function promptIfPiecesAdded(before: PiecesHolder, after: PiecesHolder) {
    const added = getAddedEdlPieces(before, after);
    if (added.length > 0) {
      setPieces(added);
      openConfirm();
    }
  }

  const confirmDialog = (
    <Confirm
      size={"small"}
      onAnswer={async (ok: boolean) => {
        if (ok && propertyId) {
          await axios.post(`/biens/${propertyId}/edl-items/generate`, {
            pieces,
          });
        }
      }}
    >
      <Text>
        Vous avez ajouté {pieces.length > 1 ? "des pièces" : "une pièce"} au
        bien : {pieces.join(", ")}. Voulez-vous
        {pieces.length > 1 ? " les" : " la"} créer aussi dans l'état des lieux ?
      </Text>
    </Confirm>
  );

  return { confirmDialog, promptIfPiecesAdded };
}
